import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { cancelableRxResource, debouncedSignal } from '@softline/core';
import {
  BadgeComponent,
  ButtonComponent,
  IconComponent,
  ItemComponent,
  ItemDescriptionComponent,
  ItemLeadingComponent,
  ItemTitleComponent,
  ItemTrailingComponent,
  LoadingSkeletonComponent,
  ResolvableModal,
  SearchInputComponent,
  SoftSlotDirective,
  SOFTLINE_MODAL_RESOLVER,
  TranslatePipe,
} from '@softline/ui-core';
import {
  DistributionTypeRow,
  DistributionTypeService,
} from '../../../../services/distribution-type.service';
import { SalesConfigService } from '../../../../services/sales-config.service';
import {
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../../../types/document-form-data';

interface DistributionTypeOption {
  id: number;
  name: string;
  subtitle: string;
  icon: string;
}

const FALLBACK_ICON = 'fa-regular fa-dolly';

function pickIcon(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('abholung') && lower.includes('werk'))
    return 'fa-regular fa-industry';
  if (lower.includes('zustellung')) return 'fa-regular fa-truck';
  if (lower.includes('abholung')) return 'fa-regular fa-box';
  return FALLBACK_ICON;
}

@Component({
  selector: 'soft-sales-delivery-type-step',
  templateUrl: './delivery-type-step.component.html',
  styleUrl: './delivery-type-step.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-w-100' },
  imports: [
    TranslatePipe,
    ButtonComponent,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemDescriptionComponent,
    ItemTrailingComponent,
    IconComponent,
    SearchInputComponent,
    SoftSlotDirective,
    BadgeComponent,
    LoadingSkeletonComponent,
  ],
})
export class DeliveryTypeStepComponent
  extends ResolvableModal<DocumentFormData>
  implements OnInit
{
  private readonly modalResolver = inject(SOFTLINE_MODAL_RESOLVER, { optional: true });
  private readonly distributionTypeService = inject(DistributionTypeService);
  private readonly salesConfig = inject(SalesConfigService).config;

  protected readonly defaultDistributionTypeId = computed<number | null>(() => {
    const raw = this.salesConfig().defaultDistributionType;
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  });

  override readonly bottomTemplate = viewChild('bottomTpl', { read: TemplateRef });

  readonly data = input.required<DocumentFormData>();
  readonly isEmbedded = input(false);
  readonly local = signal<DocumentFormData>(EMPTY_DOCUMENT_FORM_DATA);
  readonly next = output<DocumentFormData>();
  readonly dataChange = output<DocumentFormData>();
  readonly create = output<DocumentFormData>();

  readonly search = signal('');
  private readonly debouncedSearch = debouncedSignal(this.search, 200);
  readonly skeletonRows = Array.from({ length: 6 });

  private readonly distributionTypesResource = cancelableRxResource({
    params: () => ({ filter: this.debouncedSearch().trim().toLowerCase() }),
    stream: () => this.distributionTypeService.query(),
  });

  private readonly allDistributionTypes = computed<DistributionTypeOption[]>(() => {
    const rows = this.distributionTypesResource.value() ?? [];
    return rows.map((r: DistributionTypeRow) => ({
      id: r.id,
      name: r.bezeichnung,
      subtitle: r.lieferart,
      icon: pickIcon(r.bezeichnung),
    }));
  });

  readonly distributionTypes = computed(() => {
    const q = this.debouncedSearch().trim().toLowerCase();
    const rows = this.allDistributionTypes();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) || r.subtitle.toLowerCase().includes(q)
    );
  });

  readonly loading = computed(
    () => this.distributionTypesResource.status() === 'loading'
  );

  protected readonly selectedId = computed(
    () => this.local().distributionTypeId ?? null
  );

  ngOnInit(): void {
    this.local.set(untracked(() => this.data()));
  }

  constructor() {
    super();
    effect(() => {
      const rows = this.allDistributionTypes();
      if (rows.length === 0) return;
      const current = this.local();
      if (!current.distributionTypeId || current.deliveryType) return;
      const match = rows.find((r) => r.id === current.distributionTypeId);
      if (!match) return;
      this.local.update((d) => ({
        ...d,
        deliveryType: match.name,
        deliveryTypeLabel: [match.subtitle, match.name].filter(Boolean).join(' · '),
      }));
      this.dataChange.emit(this.local());
    });
  }

  selectDistributionType(option: DistributionTypeOption): void {
    this.local.update((d) => ({
      ...d,
      deliveryType: option.name,
      deliveryTypeLabel: [option.subtitle, option.name].filter(Boolean).join(' · '),
      distributionTypeId: option.id,
    }));
    this.dataChange.emit(this.local());
    if (!this.isEmbedded()) {
      this.confirm();
    }
  }

  set(key: keyof DocumentFormData, value: unknown): void {
    this.local.update((d) => ({ ...d, [key]: value }));
    this.dataChange.emit(this.local());
  }

  confirm(): void {
    const result = this.local();
    if (this.modalResolver && !this.isEmbedded()) {
      this.resolve(result);
    } else {
      this.next.emit(result);
    }
  }
}
